import { useMutation } from "@tanstack/react-query";

import { useEffectOnUpdate } from "@util/hooks";

import type { NetworkInfo } from "@alch/dx-entities";
import { buildComposerRequestUrl } from "@features/composer/lib/request";
import type { ComposerFormState } from "./lib/form.js";
import type { ComposerMethodConfig } from "./lib/methods.js";
import { nullifyEmptyValues } from "./lib/sharing.js";

interface UseMakeComposerRequestProps {
  methodConfig?: ComposerMethodConfig;
  networkInfo?: NetworkInfo;
}

const useMakeComposerRequest = ({
  methodConfig,
  networkInfo,
}: UseMakeComposerRequestProps) => {
  const mutation = useMutation<
    { body: string; ok: boolean },
    Error,
    ComposerFormState
  >({
    mutationFn: async (state) => {
      if (!methodConfig) throw Error("Incomplete request");

      const requestUrl = buildComposerRequestUrl({
        methodConfig,
        networkInfo,
        apiKey: state.app,
        query: state.query,
      });

      const stringifiedBody = methodConfig.requestBody
        ? JSON.stringify(nullifyEmptyValues(state.body))
        : undefined;

      const response = await fetch(requestUrl, {
        method: methodConfig.httpMethod,
        body: stringifiedBody,
        headers: methodConfig.requestBody
          ? { "content-type": "application/json" }
          : undefined,
      });

      const body = await response.text();

      return { body, ok: response.ok };
    },
    retry: false,
  });

  useEffectOnUpdate(() => {
    mutation.reset();
  }, [methodConfig, networkInfo?.id]);

  return mutation;
};

export default useMakeComposerRequest;
